var UTM_COOKIE = "The_Boy_Who_Lived"
var REGISTERED_COOKIE = "Has_Come_To_Die"
// Which internal page hosted the CTA the visitor clicked, and which block on
// it. Kept in its OWN cookie, deliberately apart from UTM_COOKIE: an internal
// CTA click is not an acquisition touch, so it must never occupy the
// first-touch slot (a filled UTM cookie blocks the next real external touch
// from being written).
var CTA_COOKIE = "Solemnly_Swear"
var CTA_PAGE_PARAM = "fm_page"
var CTA_BLOCK_PARAM = "fm_cta"
// utm_source values our own pages put on links that never leave the product
// (landing -> app, blog -> app, app -> landing). The visitor is already here,
// so these answer "which of our pages convinced them", never "where did they
// come from", and must not be written as attribution in any cookie state.
//
// Kept as a guard even though our own links now emit fm_page/fm_cta instead:
// people copy a signup URL out of the address bar and post it elsewhere, so
// a genuinely external visitor can still arrive carrying these values.
//
// Tie-breaker when unsure whether an inbound source is internal: do NOT
// write it. A missing utm still lets PostHog/GA classify the visit by
// referrer; a written internal source destroys that classification for good
// (observed: 121 people permanently stuck in channel "Unknown").
var INTERNAL_UTM_SOURCES = ["landing", "blog", "app"]

// Links we place in artefacts that LEAVE our systems - an exported PDF is
// mailed to a crew that has never heard of us, so a click on it is a real
// acquisition and deserves a real utm_source. What it must never do is
// overwrite attribution we already hold: the person who exported the PDF is
// usually our own user reopening their own file, and that reopen is not a
// new acquisition.
//
// Measured over 180 days before this split: of 30 signups tagged pdf_export,
// 24 had no referrer at all (PostHog called them "Direct") - the tag was the
// only thing that knew where they came from - while 6 were organic-search
// visitors whose real first touch the tag had overwritten. Hence: write on
// an empty slot, never over a filled one.
var FIRST_TOUCH_ONLY_SOURCES = ["pdf_export"]
// Click identifiers from major ad networks. gbraid/wbraid are Google Ads
// iOS 14+ replacements for gclid (gbraid for in-app, wbraid for Safari ITP),
// msclkid is Microsoft/Bing, ttclid is TikTok, li_fat_id is LinkedIn (also
// set as a first-party cookie by the LinkedIn Insight Tag - URL capture is
// a fallback for campaign types that append it), oppref is OpenAI Ads
// (ChatGPT) - their privacy-preserving click id appended to ad landing
// URLs; the OpenAI measurement pixel (GTM) also drops it into a first-party
// `__oppref` cookie, salvaged below like gclid. Without them in this
// regex, users arriving via "?gbraid=..." alone (no utm_*) leave no
// attribution trail and Google Ads server-side conversions for iOS lose
// their match. `utm_` covers the standard utm_source/medium/campaign/
// term/content/id family. `utm_id` is GA4's native manual-tagging param
// that maps to the reserved campaign_id dimension - kept implicitly via
// the `utm_` prefix match.
var UTM_PATTERN = /(utm_|fbclid|gclid|gbraid|wbraid|msclkid|ttclid|li_fat_id|ucc|campaign_id|gad_source|oppref)/

// The subset of UTM_PATTERN that actually declares WHERE the visitor came
// from. The rest (utm_campaign/term/content/id, ucc, campaign_id) only labels
// a channel: on its own it answers nothing, yet writing it FILLS the
// first-touch slot and blocks the next real external touch for good. So a URL
// tagged `?utm_campaign=summer` with no source is left unwritten - same
// tie-breaker as INTERNAL_UTM_SOURCES above, and the same rule the backend
// applies in common/utils/analytics.py#_CHANNEL_KEYS.
var CHANNEL_PATTERN =
  /^(utm_source|utm_medium|gad_source|gad_campaignid|gclid|gbraid|wbraid|fbclid|msclkid|ttclid|li_fat_id|oppref)$/

// Everything the cookie is allowed to carry. ANCHORED and explicit, because this is an
// allowlist and `UTM_PATTERN` is a substring test: unanchored, a key like `user_gclid_note`
// would pass. The set mirrors `_ATTRIBUTION_KEYS` in the backend's common/utils/analytics.py
// minus `fm_page`/`fm_cta`, which live in CTA_COOKIE and never belong here - the two MUST
// stay identical, or a param the cookie keeps is silently dropped when the backend stores it.
var ATTRIBUTION_PATTERN =
  /^(utm_(source|medium|campaign|term|content|id)|gclid|gbraid|wbraid|fbclid|msclkid|ttclid|li_fat_id|oppref|gad_source|gad_campaignid|campaign_id|ucc)$/

function getCookieDomain() {
  if (typeof window === "undefined") return ".filmustage.com"
  return window.location.hostname.includes("localhost") ? "localhost" : ".filmustage.com"
}

function setCookie(name, value) {
  var expires = "; expires=Fri, 31 Dec 9999 23:59:59 GMT"
  document.cookie =
    name + "=" + encodeURIComponent(value) + expires + "; path=/; domain=" + getCookieDomain() +
    (getCookieDomain() === "localhost" ? "; SameSite=Lax" : "; SameSite=Lax; Secure")
}

function getCookie(name) {
  var matches = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, "\\$1") + "=([^;]*)"),
  )
  return matches ? decodeURIComponent(matches[1]) : undefined
}

/**
 * Does the query name a channel - a source, a medium, or an ad click ID?
 *
 * Matching keys rather than the raw string: `?return_to=/utm_source/help`
 * contains "utm_source" as text but carries no attribution, and testing the
 * raw string would store the whole junk query as if it were a touch.
 */
function _hasChannelKey(queryString) {
  if (!queryString) return false

  var params = new URLSearchParams(queryString)
  var named = false
  // A channel key with an EMPTY value names nothing. `?utm_source=` used to pass, which
  // filled the first-touch slot with a value the backend then discards as blank - so the
  // visit was recorded nowhere AND the next real touch found the slot taken. Repeated
  // params are honoured: `?gclid=&gclid=abc` still carries a click id.
  params.forEach(function (value, key) {
    if (CHANNEL_PATTERN.test(key) && String(value).trim() !== "") named = true
  })

  return named
}

/**
 * Keep only the params the cookie is allowed to hold (see ATTRIBUTION_PATTERN).
 *
 * A landing URL carries whatever the page put there - a return path, an invite token, an
 * email in a one-click signup link. Written whole, the cookie hands all of it to
 * `readAttributionQuery`, which feeds the PostHog person profile and the backend. The
 * backend now filters on its side, but a super property registered in the browser does not
 * pass through it: the value would be on the profile before anything could drop it.
 */
function _attributionOnly(queryString) {
  var params = new URLSearchParams(queryString)
  var kept = []
  params.forEach(function (value, key) {
    if (ATTRIBUTION_PATTERN.test(key) && String(value).trim() !== "") {
      kept.push(key + "=" + encodeURIComponent(value))
    }
  })

  return kept.length ? "?" + kept.join("&") : ""
}

/**
 * Drop every `utm_*` label from a query, keeping click IDs and everything
 * else. Used for internal CTA links: the labels are ours and would claim an
 * acquisition that never happened, but a click ID riding on the same URL is
 * a real ad click and must survive.
 */
function _withoutCampaignLabels(queryString) {
  var params = new URLSearchParams(queryString)
  var kept = []
  params.forEach(function (value, key) {
    if (key.indexOf("utm_") !== 0) kept.push(key + "=" + encodeURIComponent(value))
  })

  return kept.length ? "?" + kept.join("&") : ""
}

/**
 * Drop the campaign labels of a query whose utm_source is one of ours
 * (INTERNAL_UTM_SOURCES); any other query comes back unchanged. The one rule for
 * both an inbound link and the value already stored in the cookie.
 */
function _withoutInternalSource(queryString) {
  var source = (new URLSearchParams(queryString).get("utm_source") || "").trim().toLowerCase()

  return INTERNAL_UTM_SOURCES.indexOf(source) !== -1 ? _withoutCampaignLabels(queryString) : queryString
}

/**
 * Persist the inbound attribution query string in the long-lived UTM cookie.
 *
 * Write rules (kept narrow on purpose - durable cross-touch click-ID
 * preservation lives on the backend, where AuthUser.utm survives browser
 * cookie wipes / ITP eviction / extension blockers, see backend
 * parse_user_utm merge logic):
 * - anything outside ATTRIBUTION_PATTERN is dropped before any of the rules
 *   below, so a return path, an invite token or an email in the landing URL
 *   cannot reach the cookie - and through it the PostHog person profile. The
 *   SAME filter is applied to the value already stored, because the cookie
 *   outlives the code that wrote it.
 * - a channel key with an EMPTY value does not count as naming one.
 * - internal utm_source (see INTERNAL_UTM_SOURCES) -> its `utm_*` labels are
 *   stripped in any cookie state, because our own CTA is not an acquisition
 *   touch. A click ID on the same URL still counts and is kept.
 * - a query naming no channel (labels only, e.g. `?utm_campaign=summer`) ->
 *   never written: it cannot say where the visitor came from, and filling the
 *   slot with it would block the next touch that can.
 * - empty cookie + external touch -> write as-is (first-touch capture)
 * - non-empty cookie + previously-registered user (`DIED`) + external touch
 *   -> overwrite with the new touch as-is; backend merges sticky click IDs
 *   against its stored copy when saving AuthUser.utm.
 * - FIRST_TOUCH_ONLY_SOURCES are exempt from that overwrite: a link out of an
 *   exported PDF is a real channel for whoever the file was mailed to, but a
 *   nothing-happened event when the person who exported it reopens their own
 *   file, and we cannot tell the two apart at click time.
 *
 * A visitor with no tracking params at all therefore keeps an EMPTY cookie -
 * that is the correct state, not a gap: PostHog and GA classify the visit
 * from the referrer (organic search / referral / direct) as long as nothing
 * overwrote it. Filling the slot with an internal source is what destroyed
 * that classification before (measured over 180 days: 1230 signups labelled
 * `landing/cta` of which 852 were in fact organic search).
 *
 * The internal guard used to compare against the single string "landing",
 * so `blog` / `app` / `pdf_export` slipped through and overwrote real
 * external sources for already-registered visitors; and because it only
 * applied to the overwrite branch, an empty cookie swallowed the internal
 * source anyway. Both holes are closed by checking the source list first.
 */
function updateUtmCookie() {
  if (typeof window === "undefined") return

  // A cookie written before the allowlist existed still holds whatever the landing URL
  // carried, and it never expires on its own (see setCookie). Cleaning it on the next page
  // load is the ONLY thing that reaches those visitors, and it has to happen before the
  // decisions below: until it does, the stale email or token stays readable to every script
  // on the page AND its mere presence makes the slot look occupied, so the next real touch
  // is refused. Idempotent - an already-clean cookie compares equal and is not rewritten.
  // A cookie written before our own CTAs stopped carrying utm_* (2026-08-18) still names an
  // internal source, and the app pushes it to the backend again on the next visit - which
  // re-stamped cleaned profiles with landing/cta. Stripped here with the same rule as an
  // inbound link.
  var storedValue = getCookie(UTM_COOKIE) || ""
  var existingValue = _withoutInternalSource(_attributionOnly(storedValue))
  if (storedValue !== existingValue) {
    setCookie(UTM_COOKIE, existingValue)
  }
  var registeredValue = getCookie(REGISTERED_COOKIE)
  var queryString = window.location.search
  var urlParams = new URLSearchParams(queryString)
  var incomingSource = (urlParams.get("utm_source") || "").trim().toLowerCase()
  var isFirstTouchOnly = FIRST_TOUCH_ONLY_SOURCES.indexOf(incomingSource) !== -1
  // Narrowed to attribution params FIRST, so nothing the page happened to carry can ride
  // into the cookie. An internal source then loses its campaign LABELS on top of that -
  // but a paid ad can legitimately point at a link someone copied out of the address bar,
  // so the gclid that Google Ads conversion matching depends on still survives.
  var candidate = _withoutInternalSource(_attributionOnly(queryString))
  var hasChannelParams = _hasChannelKey(candidate)
  // Judged on the CLEANED value: a cookie holding nothing but disallowed params is an empty
  // slot, not an occupied one.
  var isEmptyCookie = existingValue === ""
  var mayOverwrite = registeredValue === "DIED" && !isFirstTouchOnly

  if (hasChannelParams && (isEmptyCookie || mayOverwrite)) {
    setCookie(UTM_COOKIE, candidate)
  }

  // Fallback: if our cookie has no gclid, salvage it from Google's _gcl_aw
  // cookie (written by GTM Conversion Linker on the original click-through).
  // Google's cookie persists 90 days, so even when the user returns with a
  // clean URL or our cookie was wiped/never written (script regression, race
  // condition, third-party blocker), backend attribution still gets the
  // gclid via parse_user_utm -> AuthUser.utm. Idempotent: no-op when our
  // cookie already has gclid.
  _appendGclidFromGoogleCookieIfMissing()

  // Same salvage for OpenAI Ads: the measurement pixel (GTM) writes the
  // click id into a first-party `__oppref` cookie on the landing that our
  // URL capture can miss (existing cookie not overwritten by the write
  // rules above, params stripped by a redirect, later navigation). Merging
  // it here keeps oppref flowing into AuthUser.utm -> parse_user_utm ->
  // the OpenAI Conversions API destination.
  _appendOpprefFromPixelCookieIfMissing()
}

/**
 * Append `param=value` to the UTM cookie unless the cookie already carries
 * that param. Shared tail of every vendor-cookie salvage below - the
 * presence guard and separator picking MUST stay identical across salvages
 * (both had real bugs: a missing `^` anchor caused a double-write, and a
 * wrong separator produced strings the backend parser can't split).
 */
function _appendParamToUtmCookieIfMissing(param, value) {
  var current = getCookie(UTM_COOKIE) || ""
  // Anchor on start-of-string OR `?`/`&` so cookies whose first param
  // is `<param>=...` (no leading `?` or `&`) are also caught.
  if (new RegExp("(?:^|[?&])" + param + "=").test(current)) return

  // `&` for any non-empty cookie. The previous rule picked `?` when the
  // cookie had no leading one, which produced `utm_source=google?gclid=X`
  // - and the backend's parse_qsl reads that as ONE pair whose value is
  // `google?gclid=X`, hiding the click id from Google Ads entirely.
  // Verified: parse_qsl("utm_source=google&gclid=X") splits correctly with
  // or without a leading `?`, so `&` is always the safe separator.
  var separator = current ? "&" : "?"
  setCookie(UTM_COOKIE, current + separator + param + "=" + encodeURIComponent(value))
}

/**
 * Read `_gcl_aw` set by GTM's Conversion Linker and merge its gclid into our
 * UTM cookie when ours is missing it. Accepts both the modern 4-segment
 * `GCL.AW.<timestamp>.<gclid>` and the legacy 3-segment `GCL.<timestamp>.<gclid>`
 * still emitted by older Conversion Linker versions. The trailing capture is
 * greedy so future encodings containing dots in the gclid keep working.
 */
function _appendGclidFromGoogleCookieIfMissing() {
  var gclAw = getCookie("_gcl_aw") || ""
  var match = gclAw.match(/^GCL\.(?:AW\.)?\d+\.(.+)$/)
  if (!match) return

  _appendParamToUtmCookieIfMissing("gclid", match[1])
}

/**
 * Read `__oppref` set by the OpenAI Ads measurement pixel and merge the
 * oppref click id into our UTM cookie when ours is missing it. Unlike
 * `_gcl_aw`, the pixel stores the raw value with no envelope to parse.
 * The pixel loads asynchronously via GTM, so on the very first landing the
 * cookie may not exist yet when this runs - the URL capture above handles
 * that visit, and this salvage catches every subsequent page load.
 */
function _appendOpprefFromPixelCookieIfMissing() {
  var oppref = getCookie("__oppref") || ""
  if (!oppref) return

  _appendParamToUtmCookieIfMissing("oppref", oppref)
}

/**
 * Persist which internal page and CTA block the visitor just clicked
 * (`?fm_page=pricing&fm_cta=hero_block`) into CTA_COOKIE.
 *
 * Last touch wins on purpose - the CTA that immediately precedes a signup is
 * the one that converted, unlike acquisition which is first-touch. Writing
 * here is always safe precisely because this cookie is separate: it can never
 * occupy the acquisition slot or block a later external touch.
 *
 * No-op when neither param is present, so ordinary navigation leaves the
 * previous CTA intact.
 */
function updateCtaCookie() {
  if (typeof window === "undefined") return

  var urlParams = new URLSearchParams(window.location.search)
  var page = urlParams.get(CTA_PAGE_PARAM)
  var block = urlParams.get(CTA_BLOCK_PARAM)
  if (!page && !block) return

  var pairs = []
  if (page) pairs.push(CTA_PAGE_PARAM + "=" + encodeURIComponent(page))
  if (block) pairs.push(CTA_BLOCK_PARAM + "=" + encodeURIComponent(block))

  setCookie(CTA_COOKIE, "?" + pairs.join("&"))
}

/**
 * Concatenate two `?a=b&c=d` fragments into one, tolerating a missing leading
 * `?`, empty inputs, and a stray trailing `&`. Kept as its own helper because
 * the backend parser splits on `?`/`&` - an accidental `<raw>&<raw>` or a
 * doubled `?` produces pairs it cannot read (a wrong separator here has been
 * a real bug before, see _appendParamToUtmCookieIfMissing).
 */
function _joinQueryStrings(left, right) {
  var a = (left || "").replace(/^\?/, "").replace(/&$/, "")
  var b = (right || "").replace(/^\?/, "").replace(/&$/, "")
  if (!a) return b ? "?" + b : ""
  if (!b) return "?" + a

  return "?" + a + "&" + b
}

/**
 * The full attribution string to hand to PostHog and to the backend: where
 * the visitor came from (UTM_COOKIE) plus which internal CTA converted them
 * (CTA_COOKIE), in one query string.
 *
 * Returns "" when the visitor carries neither - callers must treat that as
 * "nothing to report" rather than sending an empty update.
 */
function readAttributionQuery() {
  if (typeof window === "undefined") return ""

  return _joinQueryStrings(getCookie(UTM_COOKIE) || "", getCookie(CTA_COOKIE) || "")
}

/**
 * What the durably-stored attribution becomes once this browser's touch is
 * folded in - or `""` when the browser carries nothing the stored copy does
 * not already hold.
 *
 * Two jobs in one pass, both needed by the caller that pushes to the backend:
 * - the `""` return is the "nothing to send" signal. Without it every page
 *   load re-pushes, because the stored copy is permanently != the cookie
 *   (the backend folds sticky click IDs into it), so a plain string compare
 *   can never settle.
 * - the returned string is what the backend will hold after its own merge,
 *   so the caller can show it to PostHog immediately instead of registering
 *   the browser's half and blanking the acquisition it could not see.
 *
 * `local` wins per key; keys only `stored` has are carried. This mirrors the
 * backend merge for every case that reaches here: a touch with labels
 * replaces the labels, a CTA-only touch leaves them alone because it has no
 * label keys to override with.
 */
function mergeAttributionQuery(stored, local) {
  var storedParams = new URLSearchParams(stored || "")
  var localParams = new URLSearchParams(local || "")

  var merged = []
  var seen = {}
  var addsSomething = false

  localParams.forEach(function (value, key) {
    if (storedParams.get(key) !== value) addsSomething = true
  })
  if (!addsSomething) return ""

  storedParams.forEach(function (value, key) {
    var effective = localParams.has(key) ? localParams.get(key) : value
    if (seen[key]) return
    seen[key] = true
    merged.push(key + "=" + encodeURIComponent(effective))
  })
  localParams.forEach(function (value, key) {
    if (seen[key]) return
    seen[key] = true
    merged.push(key + "=" + encodeURIComponent(value))
  })

  return merged.length ? "?" + merged.join("&") : ""
}

module.exports = {
  setCookie,
  getCookie,
  updateUtmCookie,
  updateCtaCookie,
  readAttributionQuery,
  mergeAttributionQuery,
  UTM_COOKIE,
  REGISTERED_COOKIE,
  CTA_COOKIE,
  CTA_PAGE_PARAM,
  CTA_BLOCK_PARAM,
  INTERNAL_UTM_SOURCES,
  FIRST_TOUCH_ONLY_SOURCES,
}
